import { StatusType } from '@datadog/browser-logs'
import { z } from 'zod/v3'
import { create } from 'zustand'
import { AppLinkingModeSchema } from '../hooks/app-link'
import { environments } from '../utils/backend'
import { zodEnumFromArray } from '../utils/zod'

type RawEnv = ImportMetaEnv | NodeJS.ProcessEnv

const envSchema = z
  .object({
    VITE_MODE: z.enum(['test', 'development', 'staging', 'production', 'amplify']),
    VITE_BACKEND_ENV: z.enum(environments).default('prod'),
    VITE_KEYCLOAK_BASE_URL: z
      .enum(['https://auth.later.com', 'https://later-upgrade.us-east-1.aws.auth.ac'])
      .default('https://auth.later.com'),
    VITE_KEYCLOAK_REALM: z.enum(['later-development', 'later-test', 'later-production']).default('later-production'),
    VITE_APP_LINKING_MODE: AppLinkingModeSchema,
    VITE_APP_URL: z.string().url(),
    VITE_AWS_IDENTITY_POOL_ID: z.string(),
    VITE_PACKAGE_VERSION: z.string().optional(),
    VITE_SEGMENT_WRITE_KEY: z.string().optional(),
    VITE_LOG_LEVEL: z.enum(zodEnumFromArray(Object.values(StatusType))).default('info'),
    VITE_DEVELOPERS: z.string().transform(csv => csv.split(',')),
    VITE_FROALA_LICENSE_KEY: z.string(),
    VITE_LOGROCKET_APP_ID: z.string().optional(),
    VITE_PRIMARY_BRAND_COLOR: z.string(),
    VITE_FACEBOOK_APP_ID: z.string(),
    VITE_FACEBOOK_SDK_URL: z.string(),
    VITE_FACEBOOK_API_VERSION: z.string(),
    VITE_FACEBOOK_OAUTH_CONFIG_ID: z.string(),
    // used in playwright tests
    KEYCLOAK_CLIENT_ID: z.string().default('playwright-service'),
    KEYCLOAK_CLIENT_SECRET: z.string().optional(),
    DEFAULT_COMMUNITY_ID: z.string().default('playwright'),
    DEFAULT_COMMUNITY_NAME: z.string().default('Playwright Test Automation'),
    OTHER_COMMUNITY_ID: z.string().default('playwrightother'),
    OTHER_COMMUNITY_NAME: z.string().default('Playwright Other'),
    INFLUENCE_MAIN_EMAIL: z.string().optional(),
    INFLUENCE_MAIN_PASSWORD: z.string().optional(),
    INFLUENCE_NON_ROOT_EMAIL: z.string().optional(),
    INFLUENCE_NON_ROOT_PASSWORD: z.string().optional(),
    INFLUENCE_OTHER_EMAIL: z.string().optional(),
    INFLUENCE_OTHER_PASSWORD: z.string().optional(),
    NON_ROOT_COMMUNITY_ID: z.string().default('playwright'),
    NON_ROOT_COMMUNITY_NAME: z.string().default('Playwright'),
  })
  .strict()

export type ValidatedEnv = z.infer<typeof envSchema>

// Define the store state type
interface EnvironmentState {
  // Raw environment data
  rawEnv: RawEnv | null

  // Derived environment state
  isProduction: boolean
  isStaging: boolean
  isLive: boolean
  isDev: boolean
  LATER_SOCIAL_URL: string
  MAVELY_CREATOR_BASE_URL: string

  validatedEnv: ValidatedEnv | null

  isInitialized: boolean
  isInitializing: boolean
  error: Error | null
  initialize: () => Promise<void>
}

function normalizeAppUrl(url: string) {
  return !url || url === 'origin' ? globalThis.location.origin : url
}

export const useEnvironmentStore = create<EnvironmentState>((set, get) => ({
  // Initial state
  rawEnv: null,
  isProduction: false,
  isStaging: false,
  isLive: false,
  isDev: true,
  LATER_SOCIAL_URL: '',
  MAVELY_CREATOR_BASE_URL: '',
  validatedEnv: null,
  isInitialized: false,
  isInitializing: false,
  error: null,

  initialize: async () => {
    // Prevent multiple initializations
    if (get().isInitialized || get().isInitializing) {
      return
    }

    set({ isInitializing: true })

    try {
      let rawEnv: RawEnv

      // Determine source of environment variables
      if (globalThis.window === undefined) {
        // Node.js environment - dynamically import
        try {
          const { loadNodeEnvironment } = await import('./environment.node.ts')
          rawEnv = await loadNodeEnvironment()
        } catch (error) {
          console.error('Failed to load environment:', error)
          throw new Error('Failed to initialize environment variables')
        }
      } else {
        // Browser environment with Vite
        rawEnv = import.meta.env
      }

      const isProduction = rawEnv.MODE === 'production'
      const isStaging = rawEnv.MODE === 'staging'
      const isLive = isProduction || isStaging
      const isDev = !isLive
      const LATER_SOCIAL_URL = `https://${isProduction ? 'app' : 'staging'}.later.com`
      const MAVELY_CREATOR_BASE_URL = isProduction ? 'https://creators.mave.ly' : 'https://creator-app-dev.vercel.app'

      // Validate environment
      const validatedEnv = validateEnv(rawEnv, isLive)

      set({
        rawEnv,
        isProduction,
        isStaging,
        isLive,
        isDev,
        LATER_SOCIAL_URL,
        MAVELY_CREATOR_BASE_URL,
        validatedEnv,
        isInitialized: true,
        isInitializing: false,
        error: null,
      })
    } catch (error) {
      console.error('Environment initialization failed:', error)
      set({
        isInitializing: false,
        error: error instanceof Error ? error : new Error(String(error)),
      })
      throw error
    }
  },
}))

function validateEnv(rawEnv: RawEnv, isLive: boolean): ValidatedEnv {
  // Create a schema with live environment specific refinements
  const refinedSchema = envSchema
    .refine(data => !(isLive && !data.VITE_SEGMENT_WRITE_KEY), {
      message: 'VITE_SEGMENT_WRITE_KEY is required when MODE is staging or production',
    })
    .refine(data => data.VITE_DEVELOPERS?.every((email: string) => z.string().email().safeParse(email).success), {
      message: 'VITE_DEVELOPERS must be a comma seperated list of emails',
    })

  const parsedEnv = refinedSchema.safeParse({
    VITE_MODE: rawEnv.MODE,
    VITE_BACKEND_ENV: rawEnv.VITE_BACKEND_ENV,
    VITE_KEYCLOAK_BASE_URL: rawEnv.VITE_KEYCLOAK_BASE_URL,
    VITE_KEYCLOAK_REALM: rawEnv.VITE_KEYCLOAK_REALM,
    VITE_APP_LINKING_MODE: rawEnv.VITE_APP_LINKING_MODE,
    VITE_APP_URL: normalizeAppUrl(String(rawEnv.VITE_APP_URL)),
    VITE_AWS_IDENTITY_POOL_ID: rawEnv.VITE_AWS_IDENTITY_POOL_ID,
    VITE_PACKAGE_VERSION: rawEnv.VITE_PACKAGE_VERSION,
    VITE_SEGMENT_WRITE_KEY: rawEnv.VITE_SEGMENT_WRITE_KEY,
    VITE_LOG_LEVEL: rawEnv.VITE_LOG_LEVEL,
    VITE_DEVELOPERS: rawEnv.VITE_DEVELOPERS,
    VITE_FROALA_LICENSE_KEY: rawEnv.VITE_FROALA_LICENSE_KEY,
    VITE_LOGROCKET_APP_ID: rawEnv.VITE_LOGROCKET_APP_ID,
    VITE_PRIMARY_BRAND_COLOR: rawEnv.VITE_PRIMARY_BRAND_COLOR,
    VITE_FACEBOOK_APP_ID: rawEnv.VITE_FACEBOOK_APP_ID,
    VITE_FACEBOOK_SDK_URL: rawEnv.VITE_FACEBOOK_SDK_URL,
    VITE_FACEBOOK_API_VERSION: rawEnv.VITE_FACEBOOK_API_VERSION,
    VITE_FACEBOOK_OAUTH_CONFIG_ID: rawEnv.VITE_FACEBOOK_OAUTH_CONFIG_ID,
  })

  if (!parsedEnv.success) {
    const errorMessage = `Environment variable validation error: ${JSON.stringify(parsedEnv.error.format(), null, 2)}`
    console.error(rawEnv)
    throw new Error(errorMessage)
  }

  return parsedEnv.data
}
